// A module-level, one-shot readiness gate for imperative data-layer callers.
//
// Consumers (tRPC links, raw fetch, axios interceptors) can `await sessionJwtIsAvailable()`
// before firing an authenticated request so they never race the httpOnly session JWT
// verification driven by SessionProvider. This is an imperative bridge on purpose:
// non-React call sites can't read the reactive `useSession()` hook, so they need a
// promise they can await.
//
// The gate opens when SessionProvider reports the session authed and settled. It stays
// open across same-account refreshes, and re-closes only when the active identity
// changes (see closeSessionReady) so callers can't race the JWT of the outgoing account.
// A fail-open timeout guarantees callers are never blocked indefinitely (e.g. an
// anonymous session, which never verifies).
//
// On the server (SSR / RSC) `sessionJwtIsAvailable` resolves immediately, so server-side
// data fetching is never gated.

// Default fail-open failsafe: if the gate is never opened (e.g. an anonymous session,
// or a validation that never settles), release blocked callers after this long.
const SESSION_READY_TIMEOUT_MS = 15_000;

type Deferred = {
  promise: Promise<void>;
  resolve: () => void;
  resolved: boolean;
};

type SessionReadyState = {
  /** The pending gate; null until a caller first awaits, resolved once the gate opens. */
  deferredAuthPromise: Deferred | null;
  /** Whether the gate has been opened — lets a late caller resolve immediately. */
  ready: boolean;
  /** Handle for the fail-open failsafe while a caller is waiting. */
  timeoutId: ReturnType<typeof setTimeout> | null;
};

const STATE_KEY = '__zapier_identity_session_ready__';

// Keep the gate on globalThis so it stays a single instance even if code-splitting
// hands a consumer's data layer a separate copy of this module (same approach as
// bridge/refreshJWT.ts) — the provider that opens it and the callers that await it
// must share one state.
const globals = globalThis as typeof globalThis &
  Record<typeof STATE_KEY, SessionReadyState | undefined>;

const isServer = (): boolean => typeof window === 'undefined';

const getState = (): SessionReadyState =>
  (globals[STATE_KEY] ??= {
    deferredAuthPromise: null,
    ready: false,
    timeoutId: null,
  });

const clearFailsafe = (state: SessionReadyState): void => {
  if (state.timeoutId !== null) {
    clearTimeout(state.timeoutId);
    state.timeoutId = null;
  }
};

/**
 * Mark the session ready: open the gate and resolve any pending promise. Idempotent.
 * Internal wiring for SessionProvider — not part of the public barrel.
 */
export function markSessionReady(): void {
  if (isServer()) return;
  const state = getState();
  clearFailsafe(state);
  state.ready = true;
  if (state.deferredAuthPromise) {
    state.deferredAuthPromise.resolved = true;
    state.deferredAuthPromise.resolve();
  }
}

/**
 * Resolves once the session JWT has been verified (or the fail-open timeout fires).
 * On the server this resolves immediately so SSR/RSC data fetching is never blocked.
 */
export function sessionJwtIsAvailable(): Promise<void> {
  if (isServer()) return Promise.resolve();
  const state = getState();
  if (state.ready) return Promise.resolve();
  // First caller to await before the session verifies: create the promise they'll wait
  // on and start the fail-open failsafe (so we never run a timer nobody is waiting on).
  if (!state.deferredAuthPromise) {
    let resolve!: () => void;
    const promise = new Promise<void>(res => {
      resolve = res;
    });
    state.deferredAuthPromise = { promise, resolve, resolved: false };
    state.timeoutId = setTimeout(markSessionReady, SESSION_READY_TIMEOUT_MS);
  }
  return state.deferredAuthPromise.promise;
}

/**
 * Re-close the gate so subsequent callers block again until it is next opened.
 * Idempotent. Internal wiring for SessionProvider — not part of the public barrel.
 *
 * SessionProvider calls this when the active identity changes. A same-tab account
 * switch reloads the page (which resets this module anyway), but a switch made in
 * another tab is picked up here via the cookie poll and re-verified in place with no
 * reload — closing the gate for that window stops a caller from firing a request
 * against the outgoing account's JWT before the new one is verified.
 */
export function closeSessionReady(): void {
  const state = getState();
  state.ready = false;
  if (state.deferredAuthPromise && !state.deferredAuthPromise.resolved) {
    // Callers are already blocked on this promise. Keep it and restart the fail-open
    // failsafe so they still resolve when the new session settles (or time out) — never
    // orphan an awaiter by dropping its promise and timer together.
    clearFailsafe(state);
    state.timeoutId = setTimeout(markSessionReady, SESSION_READY_TIMEOUT_MS);
    return;
  }
  // No pending awaiters (gate was open, or its promise already resolved): drop the stale
  // deferred so the next caller creates a fresh one that blocks until the reopen.
  clearFailsafe(state);
  state.deferredAuthPromise = null;
}

/** Reset the gate to its initial (unopened) state. Intended for tests. */
export function resetSessionReady(): void {
  const state = getState();
  clearFailsafe(state);
  state.deferredAuthPromise = null;
  state.ready = false;
}
